import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { updateUserSchema } from '@core/schemas/users';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { useUpdateUser } from '@/hooks/users';
import axios from 'axios';
import type { User } from './types';

type EditFormData = z.infer<typeof updateUserSchema>;

export function EditUserForm({
  user,
  onSaved,
  onCancel,
}: {
  user: User;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<EditFormData>({
    resolver: zodResolver(updateUserSchema),
    defaultValues: { name: user.name, email: user.email, role: user.role },
  });

  const mutation = useUpdateUser(user.id);

  const onSubmit = (data: EditFormData) =>
    mutation.mutate(data, {
      onSuccess: onSaved,
      onError: (err) => {
        const message = axios.isAxiosError(err)
          ? (err.response?.data?.error ?? 'Failed to update user')
          : 'Failed to update user';
        setError('root', { message });
      },
    });

  return (
    <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-3'>
      <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3'>
        <div className='flex flex-col gap-1'>
          <Label htmlFor={`edit-name-${user.id}`} className='text-xs'>Name</Label>
          <Input id={`edit-name-${user.id}`} {...register('name')} aria-invalid={!!errors.name} />
          {errors.name && <p className='text-destructive text-xs'>{errors.name.message}</p>}
        </div>
        <div className='flex flex-col gap-1'>
          <Label htmlFor={`edit-email-${user.id}`} className='text-xs'>Email</Label>
          <Input id={`edit-email-${user.id}`} type='email' {...register('email')} aria-invalid={!!errors.email} />
          {errors.email && <p className='text-destructive text-xs'>{errors.email.message}</p>}
        </div>
        <div className='flex flex-col gap-1'>
          <Label htmlFor={`edit-role-${user.id}`} className='text-xs'>Role</Label>
          <select
            id={`edit-role-${user.id}`}
            {...register('role')}
            className='border border-input rounded-md px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring'
          >
            <option value='AGENT'>Agent</option>
            <option value='ADMIN'>Admin</option>
          </select>
        </div>
      </div>
      {errors.root && (
        <Alert variant='destructive'>
          <AlertCircle className='h-4 w-4' />
          <AlertDescription>{errors.root.message}</AlertDescription>
        </Alert>
      )}
      <div className='flex gap-2'>
        <Button type='submit' size='sm' disabled={mutation.isPending}>
          {mutation.isPending ? 'Saving...' : 'Save'}
        </Button>
        <Button type='button' size='sm' variant='outline' onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
